import React, { useCallback, useMemo } from 'react';
import { Chessboard } from 'react-chessboard';
import { GameLogic } from '../../services/GameLogic';
import {
  BOARD_LIGHT_SQUARE, BOARD_DARK_SQUARE, LAST_MOVE_COLOR, SELECTED_COLOR,
  MOVE_DOT, CAPTURE_RING, NOTATION_LIGHT, NOTATION_DARK
} from '../../theme';

/**
 * ChessBoard Component
 * Single Responsibility: Rendering chess board and handling piece interactions
 * Interface Segregation: Only knows about chess board rendering
 */
// Small badges drawn in the top-right corner of the destination square
const badgeImage = (type) => {
  const color = type === 'correct' ? '#22c55e' : '#ef4444';
  const icon = type === 'correct'
    ? '<path d="M7 12.5l3 3 7-7" stroke="white" stroke-width="2.6" fill="none" stroke-linecap="round" stroke-linejoin="round"/>'
    : '<path d="M8 8l8 8M16 8l-8 8" stroke="white" stroke-width="2.6" stroke-linecap="round"/>';
  return `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><rect x="1" y="1" width="22" height="22" rx="5" fill="${color}"/>${icon}</svg>`
  )}")`;
};


const ChessBoard = ({ 
  game, 
  selectedSquare, 
  possibleMoves, 
  lastMove,
  moveBadge,
  playerColor,
  showCelebration,
  onSquareClick,
  onPieceDrop 
}) => {
  
  const squareStyles = useMemo(() => {
    const styles = {};
    const addStyle = (square, style) => {
      styles[square] = { ...styles[square], ...style };
    };
    
    // Last move: soft highlight of both squares
    if (lastMove) {
      addStyle(lastMove.from, { backgroundColor: LAST_MOVE_COLOR });
      addStyle(lastMove.to, { backgroundColor: LAST_MOVE_COLOR });
    }
    
    // Selected piece
    if (selectedSquare) {
      addStyle(selectedSquare, { backgroundColor: SELECTED_COLOR });
    }
    
    // Possible moves: discreet dots, rings on captures
    const layers = {};
    possibleMoves.forEach(square => {
      layers[square] = [game.get(square) ? CAPTURE_RING : MOVE_DOT];
      addStyle(square, { cursor: 'pointer' });
    });
    
    // Correct / wrong badge in the corner of the destination square
    if (moveBadge) {
      layers[moveBadge.square] = [badgeImage(moveBadge.type), ...(layers[moveBadge.square] || [])];
    }
    
    Object.entries(layers).forEach(([square, images]) => {
      const isBadge = moveBadge?.square === square;
      addStyle(square, {
        backgroundImage: images.join(', '),
        backgroundRepeat: 'no-repeat',
        backgroundSize: images.map((_, index) => (isBadge && index === 0 ? '30% 30%' : '100% 100%')).join(', '),
        backgroundPosition: images.map((_, index) => (isBadge && index === 0 ? 'top 4% right 4%' : 'center')).join(', ')
      });
    });
    
    return styles;
  }, [lastMove, moveBadge, selectedSquare, possibleMoves, game]);

  return (
    <div 
      className="aspect-square rounded-xl overflow-hidden shadow-board ring-1 ring-black/30"
      style={{
        // Fill the available space without pushing the controls off screen
        width: 'min(calc(100vw - 24px), max(calc(100dvh - 210px), 280px), 760px)',
        transform: showCelebration ? 'scale(1.01)' : 'scale(1)',
        transition: 'transform 0.3s ease'
      }}
    >
      <Chessboard 
        options={{
          position: game.fen(),
          onPieceDrop: onPieceDrop,
          onSquareClick: onSquareClick,
          allowDrawingArrows: false,
          allowDragging: true,
          squareStyles,
          boardOrientation: playerColor || 'white',
          lightSquareStyle: BOARD_LIGHT_SQUARE,
          darkSquareStyle: BOARD_DARK_SQUARE,
          lightSquareNotationStyle: NOTATION_LIGHT,
          darkSquareNotationStyle: NOTATION_DARK,
          animationDurationInMs: 160
        }}
      />
    </div>
  );
};

/**
 * Chess Interaction Handler
 * Single Responsibility: Converting UI interactions to game actions
 */
export const useChessInteraction = (gameState, uiState, moveHandler, opening) => {
  const { game } = gameState;
  const { selectedSquare, setSelectedSquare, setPossibleMoves, clearSelection } = uiState;

  const onSquareClick = useCallback(({ square }) => {
    const piece = game.get(square);

    // No piece selected - try to select
    if (selectedSquare === null) {
      const playerColor = gameState.currentLine?.playerColor || opening?.playerColor;
      if (GameLogic.isPieceMovableByPlayer(piece, playerColor)) {
        setSelectedSquare(square);
        const moves = GameLogic.getPossibleMoves(game, square);
        setPossibleMoves(moves);
      }
      return;
    }

    // Same square clicked - deselect
    if (selectedSquare === square) {
      clearSelection();
      return;
    }

    // Attempt move
    const result = moveHandler.attemptMove(selectedSquare, square);
    
    if (result.success || result.type === 'wrong_move') {
      clearSelection();
    } else {
      // Try to select new piece if move failed
      const playerColor = gameState.currentLine?.playerColor || opening?.playerColor;
      if (GameLogic.isPieceMovableByPlayer(piece, playerColor)) {
        setSelectedSquare(square);
        const moves = GameLogic.getPossibleMoves(game, square);
        setPossibleMoves(moves);
      } else {
        clearSelection();
      }
    }

    // Set feedback
    uiState.setFeedback(result.data.message || '');
    
  }, [game, selectedSquare, gameState, uiState, moveHandler, opening, setSelectedSquare, setPossibleMoves, clearSelection]);

  const onPieceDrop = useCallback(({ sourceSquare, targetSquare }) => {
    clearSelection();
    const result = moveHandler.attemptMove(sourceSquare, targetSquare);
    uiState.setFeedback(result.data.message || '');
    // A wrong move stays on the board until it is taken back
    return result.success || result.type === 'wrong_move';
  }, [moveHandler, clearSelection, uiState]);

  return { onSquareClick, onPieceDrop };
};

export default ChessBoard;